'use client';

import classNames from 'classnames';
import { FC, ReactNode, useCallback, useMemo } from 'react';

import { PropsWithClassname } from '@/app/types';

export interface PercentageBreakpointsInputOption {
  value: number;
  label: string;
  note: ReactNode;
}

export interface PercentageBreakpointsInputProps extends PropsWithClassname {
  value: number;
  onChange: (nextValue: number) => void;
  options: PercentageBreakpointsInputOption[];
  disabled?: boolean;
  valueDisplay: ReactNode;
}

const PercentageBreakpointsInput: FC<PercentageBreakpointsInputProps> = ({
  onChange,
  options,
  disabled,
  className,
  value,
  valueDisplay,
}) => {
  const renderedNote = useMemo(() => {
    return options.find((option) => option.value === value)?.note;
  }, [value, options]);

  const isFirstValueSelected = useMemo(() => value === options[0].value, [value, options]);

  const isLastValueSelected = useMemo(() => value === options[options.length - 1].value, [value, options]);

  const handlePlusButtonClick = useCallback(() => {
    if (isLastValueSelected || typeof value !== 'number') {
      return;
    }

    const currentlySelectedOptionIndex = options.findIndex((option) => option.value === value);
    const nextSelectedOption = options[currentlySelectedOptionIndex + 1];

    onChange(nextSelectedOption.value);
  }, [value, onChange, options, isLastValueSelected]);

  const handleMinusButtonClick = useCallback(() => {
    if (isFirstValueSelected || typeof value !== 'number') {
      return;
    }

    const currentlySelectedOptionIndex = options.findIndex((option) => option.value === value);
    const nextSelectedOption = options[currentlySelectedOptionIndex - 1];

    onChange(nextSelectedOption.value);
  }, [value, onChange, options, isFirstValueSelected]);

  return (
    <div className={classNames(className, '')}>
      <div className="flex justify-center items-center">
        <button
          type="button"
          data-testid="tip-select-minus"
          onClick={handleMinusButtonClick}
          disabled={disabled || isFirstValueSelected || typeof value !== 'number'}
          className={classNames('w-[40px] h-[40px] rounded-full text-[24px] transition-all', {
            'bg-primary-light hover:shadow-md': !isFirstValueSelected && typeof value === 'number',
            'bg-neutral-light': isFirstValueSelected || typeof value !== 'number',
          })}
        >
          -
        </button>
        <div className="w-[100px] text-center text-[20px]" data-testid="tip-select-value">
          {valueDisplay}
        </div>
        <button
          data-testid="tip-select-plus"
          type="button"
          onClick={handlePlusButtonClick}
          disabled={disabled || isLastValueSelected || typeof value !== 'number'}
          className={classNames('w-[40px] h-[40px] rounded-full text-[24px] transition-all', {
            'bg-primary-light hover:shadow-md': !isLastValueSelected && typeof value === 'number',
            'bg-neutral-light': isLastValueSelected || typeof value !== 'number',
          })}
        >
          +
        </button>
      </div>
      <div className="tip-route-arrow relative p-[24px] border border-neutral-light rounded mt-[16px] shadow-sm">
        {renderedNote}
      </div>
    </div>
  );
};

export default PercentageBreakpointsInput;
